import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  Boxes, Upload, Sparkles, Image as ImageIcon, Trash2, Download, Save,
  Loader2, X, Wand2, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// AdminVisualizer — Gemini Nano Banana image-to-image renderer.
// Admin uploads a photo of a space + describes a finish/style → AI returns a
// photoreal render. Save to gallery, download, delete. Side-by-side before/after.
// ---------------------------------------------------------------------------
const AdminVisualizer = () => {
  const fileRef = useRef(null);
  const [sourceFile, setSourceFile] = useState(null);
  const [sourcePreview, setSourcePreview] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [title, setTitle] = useState('Untitled render');
  const [presets, setPresets] = useState([]);
  const [rendering, setRendering] = useState(false);
  const [renderResult, setRenderResult] = useState(null); // {source_url, render_url, prompt}
  const [renders, setRenders] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
  });

  const fetchRenders = useCallback(async () => {
    setGalleryLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/visualizer/renders`, { headers: authHeaders() });
      setRenders(r.data || []);
    } catch (err) {
      toast.error('Could not load gallery');
    } finally {
      setGalleryLoading(false);
    }
  }, []);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/visualizer/presets`, { headers: authHeaders() })
      .then((r) => setPresets(r.data?.presets || []))
      .catch(() => {});
    fetchRenders();
  }, [fetchRenders]);

  const handleSourcePick = (file) => {
    if (!file) return;
    setSourceFile(file);
    setSourcePreview(URL.createObjectURL(file));
    setRenderResult(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleSourcePick(f);
  };

  const handleRender = async () => {
    if (!sourceFile) {
      toast.error('Pick a source photo first');
      return;
    }
    if ((prompt || '').trim().length < 5) {
      toast.error('Describe the finish (at least 5 characters)');
      return;
    }
    setRendering(true);
    setRenderResult(null);
    try {
      const fd = new FormData();
      fd.append('prompt', prompt.trim());
      fd.append('source', sourceFile);
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/visualizer/render`,
        fd,
        { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' }, timeout: 180000 },
      );
      setRenderResult(res.data);
      toast.success('Render complete');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Render failed');
    } finally {
      setRendering(false);
    }
  };

  const handleSave = async () => {
    if (!renderResult) return;
    setSaving(true);
    try {
      const payload = {
        title: title || 'Untitled render',
        source_url: renderResult.source_url,
        source_mime: renderResult.source_mime,
        render_url: renderResult.render_url,
        prompt: renderResult.prompt,
      };
      await axios.post(
        `${BACKEND_URL}/api/admin/visualizer/renders`,
        payload,
        { headers: authHeaders() },
      );
      toast.success('Saved to gallery');
      await fetchRenders();
    } catch (err) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/visualizer/renders/${confirmDelete.id}`,
        { headers: authHeaders() },
      );
      toast.success('Deleted');
      setConfirmDelete(null);
      fetchRenders();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handleDownload = (url, filename = 'render.png') => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const reset = () => {
    setSourceFile(null);
    setSourcePreview(null);
    setRenderResult(null);
    setPrompt('');
    setTitle('Untitled render');
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Boxes className="h-8 w-8" style={{ color: '#00CED1' }} />
          <div>
            <h1 className="text-3xl font-bold" data-testid="admin-visualizer-heading">3D Visualizer</h1>
            <p className="text-sm text-gray-500">
              Drop a photo, describe a finish, watch Gemini Nano Banana re-imagine the space.
            </p>
          </div>
        </div>
        <Badge variant="outline" className="text-xs">AI image-to-image</Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Source panel */}
        <Card data-testid="admin-visualizer-source-card">
          <CardContent className="p-5 space-y-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              1 · Source photo
            </h2>
            {!sourcePreview && (
              <div
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors hover:border-teal-400 hover:bg-teal-50/40"
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                data-testid="admin-visualizer-dropzone"
              >
                <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm font-semibold text-gray-700">Drop a photo or click to browse</p>
                <p className="text-xs text-gray-500 mt-1">PNG / JPG / WEBP · up to 12 MB</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => handleSourcePick(e.target.files?.[0])}
                  data-testid="admin-visualizer-file-input"
                />
              </div>
            )}
            {sourcePreview && (
              <div className="relative" data-testid="admin-visualizer-source-preview">
                <img src={sourcePreview} alt="Source" className="w-full rounded-lg border" />
                <button
                  type="button"
                  onClick={reset}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow"
                  data-testid="admin-visualizer-clear"
                  title="Remove source photo"
                >
                  <X className="h-4 w-4 text-gray-700" />
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Prompt panel */}
        <Card data-testid="admin-visualizer-prompt-card">
          <CardContent className="p-5 space-y-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              2 · Describe the finish
            </h2>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. matte navy paint, brass sconce, oak skirting, marble countertop, warm Scandinavian feel"
              rows={5}
              data-testid="admin-visualizer-prompt"
            />
            {presets.length > 0 && (
              <div className="space-y-1.5" data-testid="admin-visualizer-presets">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
                  Or pick a preset:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {presets.map((p, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPrompt(p.prompt)}
                      className="text-[11px] px-2 py-1 rounded-full border border-gray-300 hover:border-teal-400 hover:bg-teal-50 text-gray-700"
                      data-testid={`admin-visualizer-preset-${i}`}
                      title={p.prompt}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Button
              onClick={handleRender}
              disabled={rendering || !sourceFile || !prompt.trim()}
              className="w-full text-white font-semibold"
              style={{ backgroundColor: '#00CED1' }}
              data-testid="admin-visualizer-render-btn"
            >
              {rendering ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating render…</>
              ) : (
                <><Wand2 className="h-4 w-4 mr-2" /> Generate render</>
              )}
            </Button>
            <p className="text-[11px] text-gray-500">
              Powered by Gemini Nano Banana · usually takes 8-20 seconds.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Before/After result */}
      {renderResult && (
        <Card className="mb-8 border-2" style={{ borderColor: '#A5F3F2' }} data-testid="admin-visualizer-result">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-2">
                <Sparkles className="h-4 w-4" style={{ color: '#00CED1' }} />
                3 · Result
              </h2>
              <div className="flex items-center gap-2">
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Render title"
                  className="w-56 h-9"
                  data-testid="admin-visualizer-result-title"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRender}
                  disabled={rendering}
                  data-testid="admin-visualizer-regenerate"
                  title="Re-run with the same prompt"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${rendering ? 'animate-spin' : ''}`} />
                  Regenerate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownload(renderResult.render_url, `${(title || 'render').replace(/\W+/g, '_')}.png`)}
                  data-testid="admin-visualizer-download"
                >
                  <Download className="h-4 w-4 mr-2" /> Download
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                  className="text-white font-semibold"
                  style={{ backgroundColor: '#00CED1' }}
                  data-testid="admin-visualizer-save"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save to gallery
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <BeforeAfter label="Before" url={renderResult.source_url} testid="admin-visualizer-before" />
              <BeforeAfter label="After" url={renderResult.render_url} testid="admin-visualizer-after" highlight />
            </div>
            <p className="text-xs text-gray-500">
              <strong>Prompt:</strong> {renderResult.prompt}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Gallery */}
      <section data-testid="admin-visualizer-gallery">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold">Saved renders</h2>
          <span className="text-xs text-gray-500">{renders.length} item(s)</span>
        </div>
        {galleryLoading && <p className="text-sm text-gray-500">Loading…</p>}
        {!galleryLoading && renders.length === 0 && (
          <Card>
            <CardContent className="p-10 text-center text-gray-500">
              No saved renders yet. Generate one above and click <strong>Save to gallery</strong>.
            </CardContent>
          </Card>
        )}
        {!galleryLoading && renders.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {renders.map((r) => (
              <Card key={r.id} data-testid={`admin-visualizer-card-${r.id}`}>
                <CardContent className="p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-1 aspect-video bg-gray-50 rounded overflow-hidden">
                    {r.source_url && <img src={r.source_url} alt="Source" className="w-full h-full object-cover" />}
                    {r.render_url && <img src={r.render_url} alt="Render" className="w-full h-full object-cover" />}
                  </div>
                  <p className="text-sm font-semibold truncate" title={r.title}>{r.title}</p>
                  <p className="text-[11px] text-gray-500 line-clamp-2" title={r.prompt}>{r.prompt}</p>
                  <div className="flex items-center justify-between text-[10px] text-gray-400">
                    <span>{r.owner} · {(r.created_at || '').slice(0, 10)}</span>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => handleDownload(r.render_url, `${(r.title || 'render').replace(/\W+/g, '_')}.png`)}
                        className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-teal-600"
                        title="Download"
                        data-testid={`admin-visualizer-card-download-${r.id}`}
                      >
                        <Download className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(r)}
                        className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                        title="Delete"
                        data-testid={`admin-visualizer-card-delete-${r.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete this render?"
        description={confirmDelete ? `"${confirmDelete.title}" will be permanently removed.` : ''}
        confirmLabel="Delete"
        destructive
        onCancel={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        testId="admin-visualizer-confirm-delete"
      />
    </AdminLayout>
  );
};

const BeforeAfter = ({ label, url, testid, highlight }) => (
  <div className="space-y-1.5" data-testid={testid}>
    <p className={`text-[10px] uppercase tracking-wider font-bold ${highlight ? 'text-teal-700' : 'text-gray-500'}`}>
      {label}
    </p>
    <div
      className={`aspect-video rounded-lg overflow-hidden border-2 bg-gray-50 ${
        highlight ? 'border-teal-300 shadow-md' : 'border-gray-200'
      }`}
    >
      {url ? (
        <img src={url} alt={label} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon className="h-8 w-8 text-gray-300" />
        </div>
      )}
    </div>
  </div>
);

export default AdminVisualizer;
